import { apiClient } from '../utils/api-client';
import type { Zone, ZoneExposure, ZoneResponse, ZonesListResponse } from '../types/zone';

export interface CreateZoneData {
  name: string;
  image_signed_id?: string;
  /** A compass point from ZONE_EXPOSURES. Crag sectors only. */
  exposure?: ZoneExposure;
}

export interface UpdateZoneData {
  name?: string;
  image_signed_id?: string;
  /** A compass point from ZONE_EXPOSURES, or '' to say it is not recorded. */
  exposure?: ZoneExposure | '' | null;
  line_thickness?: number;
  line_corridor?: number;
}

/**
 * Zones are nested under the location that owns them, and each subclass has its
 * own segment (`/api/gyms/...`, `/api/crags/...`, `/api/competitions/...`) even
 * though one controller serves all three.
 */
function zonesBasePath(locationType: string): string {
  switch (locationType.toLowerCase()) {
    case 'crag':
      return 'crags';
    case 'competition':
      return 'competitions';
    default:
      return 'gyms';
  }
}

class ZonesService {
  async getZonesForLocation(slug: string, locationType: string = 'gym'): Promise<Zone[]> {
    const response = await apiClient.get(`/api/${zonesBasePath(locationType)}/${slug}/zones`);

    if (!response.ok) {
      throw new Error('Failed to fetch zones');
    }

    const data: ZonesListResponse = await response.json();
    return data.data.map(item => item.attributes);
  }

  /** @deprecated Use getZonesForLocation instead */
  async getZonesForGym(gymSlug: string): Promise<Zone[]> {
    return this.getZonesForLocation(gymSlug, 'gym');
  }

  async getZone(gymSlug: string, zoneSlug: string, locationType: string = 'gym'): Promise<Zone> {
    const response = await apiClient.get(
      `/api/${zonesBasePath(locationType)}/${gymSlug}/zones/${zoneSlug}`
    );

    if (!response.ok) {
      throw new Error('Zone not found');
    }

    const data: ZoneResponse = await response.json();
    return data.data.attributes;
  }

  async createZone(gymSlug: string, zoneData: CreateZoneData, locationType: string = 'gym'): Promise<Zone> {
    const response = await apiClient.post(
      `/api/${zonesBasePath(locationType)}/${gymSlug}/zones`,
      { data: { attributes: zoneData } }
    );

    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as { errors?: Array<{ detail: string }> };
      throw new Error(err.errors?.[0]?.detail || 'Failed to create zone');
    }

    const data: ZoneResponse = await response.json();
    return data.data.attributes;
  }

  async updateZone(
    gymSlug: string,
    zoneSlug: string,
    zoneData: UpdateZoneData,
    locationType: string = 'gym'
  ): Promise<Zone> {
    const response = await apiClient.patch(
      `/api/${zonesBasePath(locationType)}/${gymSlug}/zones/${zoneSlug}`,
      { data: { attributes: zoneData } }
    );

    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as { errors?: Array<{ detail: string }> };
      throw new Error(err.errors?.[0]?.detail || 'Failed to update zone');
    }

    const data: ZoneResponse = await response.json();
    return data.data.attributes;
  }

  async deleteZone(gymSlug: string, zoneSlug: string, locationType: string = 'gym'): Promise<void> {
    const response = await apiClient.delete(
      `/api/${zonesBasePath(locationType)}/${gymSlug}/zones/${zoneSlug}`
    );

    if (!response.ok) {
      throw new Error('Failed to delete zone');
    }
  }

  async reorderZones(locationSlug: string, zoneIds: string[], locationType: string = 'gym'): Promise<Zone[]> {
    const response = await apiClient.post(
      `/api/${zonesBasePath(locationType)}/${locationSlug}/zones/reorder`,
      { zone_ids: zoneIds }
    );

    if (!response.ok) {
      throw new Error('Failed to reorder zones');
    }

    const data: ZonesListResponse = await response.json();
    return data.data.map(item => item.attributes);
  }
}

export const zonesService = new ZonesService();
